/**
 * Chat experience strings ('chat.*'): sidebar, transcript, composer, share,
 * delete, command palette, shortcuts, feedback/voice controls, chart UI and
 * inline error messages. The Arabic table must cover every key.
 */
const en = {
  // Sidebar
  'chat.newChat': 'New Chat',
  'chat.searchChats': 'Search chats',
  'chat.noChatsFound': 'No chats found',
  'chat.noHistory': 'No chat history yet',
  'chat.startConversation': 'Start a new conversation!',
  'chat.chatTitle': 'Chat title',
  'chat.pinChat': 'Pin chat',
  'chat.unpinChat': 'Unpin chat',
  'chat.pinChatNamed': 'Pin chat: {title}',
  'chat.unpinChatNamed': 'Unpin chat: {title}',
  'chat.renameChat': 'Rename chat',
  'chat.renameChatNamed': 'Rename chat: {title}',
  'chat.deleteChat': 'Delete chat',
  'chat.deleteChatNamed': 'Delete chat: {title}',
  'chat.inMessages': 'In messages',
  'chat.searching': 'searching…',
  'chat.matches': '{n} matches',

  // Session date groups
  'chat.group.pinned': 'Pinned',
  'chat.group.today': 'Today',
  'chat.group.yesterday': 'Yesterday',
  'chat.group.week': 'Previous 7 days',
  'chat.group.month': 'Previous 30 days',

  // Main area
  'chat.dropFile': 'Drop file here to attach',
  'chat.openSidebar': 'Open sidebar',
  'chat.closeSidebar': 'Close sidebar',
  'chat.shareChat': 'Share chat',
  'chat.share': 'Share',
  'chat.noAgentsTitle': 'No AI Agents Available',
  'chat.noAgentsBody': 'Please contact your administrator to get access to AI agents.',
  'chat.greet.morning': 'Good Morning',
  'chat.greet.afternoon': 'Good Afternoon',
  'chat.greet.evening': 'Good Evening',
  'chat.greet.helpToday': 'How can I help you today?',
  'chat.greet.assistTonight': 'How can I assist you tonight?',
  'chat.greet.workingLate': "Working late? I'm here to help.",
  'chat.selectAgentHint': 'Select an agent above to start chatting',
  'chat.aiAssistant': 'AI Assistant',
  'chat.aiAgent': 'AI Agent',
  'chat.assistantResponding': 'Assistant is responding',
  'chat.assistantThinking': 'Assistant is thinking',
  'chat.scrollToBottom': 'Scroll to bottom',
  'chat.you': 'You',

  // Suggested prompts
  'chat.prompt.legal1': 'Review this contract and flag any unusual indemnity or liability clauses',
  'chat.prompt.legal2': 'Summarise the key obligations, milestones and payment terms',
  'chat.prompt.legal3': 'Compare these two agreements and list every material difference',
  'chat.prompt.legal4': 'Draft a non-disclosure agreement for a new subcontractor',
  'chat.prompt.default1': 'Summarise the attached document and pull out the action items',
  'chat.prompt.default2': 'Turn these meeting notes into a structured task list with owners',
  'chat.prompt.default3': 'Explain this topic to me as if I were briefing an executive',
  'chat.prompt.default4': 'Help me draft a professional email to a client about a delay',

  // Messages
  'chat.copyMessage': 'Copy message',
  'chat.messageCopied': 'Message copied to clipboard',
  'chat.editMessage': 'Edit message',
  'chat.edited': '(edited)',
  'chat.deleteMessage': 'Delete message',
  'chat.showFull': 'Show full response',
  'chat.showLess': 'Show less',

  // Composer
  'chat.messagePlaceholder': 'Message {name}...',
  'chat.selectAgentPlaceholder': 'Select an agent to start chatting...',
  'chat.addFiles': 'Add files',
  'chat.connectors': 'Connectors',
  'chat.composerTools': 'Add files and tools',
  'chat.stopGenerating': 'Stop generating',
  'chat.sendMessage': 'Send message',
  'chat.disclaimer': '{name} is AI and can make mistakes. Please double-check responses.',
  'chat.dismiss': 'Dismiss',

  // Agent switcher
  'chat.agent.changeAria': 'Agent: {name}. Change agent',
  'chat.agent.talkingTo': 'Talking to {name}',
  'chat.agent.choose': 'Choose an agent',
  'chat.agent.chooseShort': 'Choose agent',
  'chat.agent.find': 'Find an agent…',
  'chat.agent.filter': 'Filter agents',
  'chat.agent.noMatch': 'No agents match “{q}”.',
  'chat.agent.list': 'Agents',

  // Attachments
  'chat.file.uploading': '{name} (uploading…)',
  'chat.file.open': 'Open {name}',
  'chat.file.remove': 'Remove',
  'chat.file.removeNamed': 'Remove {name}',
  'chat.file.analyzing': 'Analyzing…',

  // Code / tables
  'chat.code.copy': 'Copy code',
  'chat.table.saved': 'Saved to your downloads',
  'chat.table.download': 'Download this table as CSV',
  'chat.table.downloaded': 'Table downloaded',
  'chat.table.downloadAria': 'Download this table as a CSV file',

  // Delete modal
  'chat.deleteSessionTitle': 'Delete Chat Session',
  'chat.closeDialog': 'Close dialog',
  'chat.deleteConfirm': 'Are you sure you want to delete this chat session?',
  'chat.messagesCount': '{n} messages',
  'chat.deleteWarning': 'This action cannot be undone. All messages in this session will be permanently deleted.',
  'chat.deleteSession': 'Delete Session',

  // Share modal
  'chat.chatWith': 'Chat with {name}',
  'chat.shareChatTitle': 'Share Chat',
  'chat.sharedChat': 'Shared chat',
  'chat.checkingShare': 'Checking sharing status…',
  'chat.shareLink': 'Share link',
  'chat.shareReadOnly': 'Read-only for anyone signed in to Nesma AI with the link · shows messages up to {date}',
  'chat.viewedTimes': ' · viewed {n} times',
  'chat.viewedOnce': ' · viewed once',
  'chat.includeNewer': 'Include {n} newer messages',
  'chat.includeNewerOne': 'Include 1 newer message',
  'chat.stopSharing': 'Stop sharing',
  'chat.shareIntro': 'Create a read-only link. Anyone signed in to Nesma AI who has it can view this chat as it is now — later messages stay private unless you update the link. Attachments are listed by name only.',
  'chat.creating': 'Creating…',
  'chat.createShareLink': 'Create share link',
  'chat.copyLink': 'Copy link',
  'chat.linkCopied': 'Copied!',
  'chat.downloadMarkdown': 'Download Markdown',
  'chat.copyConversation': 'Copy conversation',
  'chat.downloadPdf': 'Download PDF',
  'chat.exporting': 'Exporting...',
  'chat.viewConversation': 'View the conversation: {url}',
  'chat.viewConversationShort': 'View the conversation',

  // Toasts
  'chat.toast.connectorOnFailed': 'Could not turn on {name}.',
  'chat.toast.connectorOffFailed': 'Could not turn off {name}.',
  'chat.toast.google.connected': 'Google Workspace connected. The agent can now use your Drive, Gmail and Calendar.',
  'chat.toast.google.partial': 'Google connected, but not every permission was approved. Only the approved parts will work.',
  'chat.toast.google.denied': 'Google sign-in was cancelled.',
  'chat.toast.google.expired': 'The Google sign-in took too long. Please try again.',
  'chat.toast.google.domain': 'Please sign in with your company Google account.',
  'chat.toast.google.browser': 'Finish the Google sign-in in the same browser you started it in.',
  'chat.toast.google.disabled': 'Connectors are not enabled for your account.',
  'chat.toast.google.limit': 'You have reached the maximum number of connectors.',
  'chat.toast.google.failed': 'Google sign-in failed. Please try again.',
  'chat.toast.newChatWith': 'Started a new chat with {name}.',
  'chat.toast.pinFailed': 'Could not update the pin. Please try again.',
  'chat.toast.deleteSessionFailed': 'Failed to delete chat session. Please try again.',
  'chat.toast.deleteFailedTitle': 'Delete failed',
  'chat.toast.noAgent': 'Pick an AI agent first — each one is tuned for a different kind of work.',
  'chat.toast.noAgentTitle': 'No agent selected',
  'chat.toast.regenerateFailed': 'Failed to regenerate response. Please try again.',
  'chat.toast.regenerateFailedTitle': 'Regenerate failed',
  'chat.toast.editFailed': 'Failed to edit message. Please try again.',
  'chat.toast.editFailedTitle': 'Edit failed',
  'chat.toast.deleteMessageFailed': 'Failed to delete message. Please try again.',
  'chat.toast.downloadFailed': 'Failed to download file.',
  'chat.toast.downloadFailedTitle': 'Download failed',
  'chat.toast.nothingToExport': 'There is nothing to export yet — send a message first.',
  'chat.toast.emptyChat': 'Empty chat',
  'chat.toast.copiedConversation': 'The whole conversation is on your clipboard as Markdown.',
  'chat.toast.clipboardBlocked': 'Your browser blocked clipboard access. Use "Download Markdown" instead.',
  'chat.toast.pdfFailed': 'Failed to export chat to PDF. Please try again.',
  'chat.toast.exportFailedTitle': 'Export failed',
  'chat.toast.nothingToShare': 'Send a message first — there is nothing to share yet.',
  'chat.toast.linkUpdated': 'The link now includes the latest messages.',
  'chat.toast.linkUpdatedTitle': 'Link updated',
  'chat.toast.shareFailed': 'Could not create a share link.',
  'chat.toast.stoppedSharing': 'The link no longer works for anyone.',
  'chat.toast.stoppedSharingTitle': 'Stopped sharing',
  'chat.toast.stopSharingFailed': 'Could not stop sharing.',

  // Command palette
  'chat.palette.label': 'Command palette',
  'chat.palette.placeholder': 'Search agents, recent chats, or commands…',
  'chat.palette.noMatches': 'No matches. Try different keywords.',
  'chat.palette.toNavigate': 'to navigate',
  'chat.palette.toSelect': 'to select',
  'chat.palette.toClose': 'to close',
  'chat.palette.group.action': 'Actions',
  'chat.palette.group.agent': 'Agents',
  'chat.palette.group.session': 'Recent chats',
  'chat.palette.group.navigation': 'Go to',
  'chat.palette.newChat': 'New chat',
  'chat.palette.newChatSub': 'Start a fresh conversation',
  'chat.palette.shortcuts': 'Keyboard shortcuts',
  'chat.palette.shortcutsSub': 'Show all available shortcuts',
  'chat.palette.switchAgent': 'Switch to this agent',
  'chat.palette.recentChat': 'Recent chat',

  // Keyboard shortcuts sheet
  'chat.shortcuts.title': 'Keyboard shortcuts',
  'chat.shortcuts.palette': 'Open command palette',
  'chat.shortcuts.newChat': 'New chat',
  'chat.shortcuts.theme': 'Toggle theme',
  'chat.shortcuts.focus': 'Focus message input',
  'chat.shortcuts.send': 'Send message',
  'chat.shortcuts.newLine': 'New line in message',
  'chat.shortcuts.stopVoice': 'Cancel voice dictation',
  'chat.shortcuts.sheet': 'Show this shortcut sheet',
  'chat.shortcuts.close': 'Close dialogs / cancel',

  // Feedback buttons
  'chat.fb.group': 'Message feedback actions',
  'chat.fb.wait': 'Wait for message to complete',
  'chat.fb.copy': 'Copy message to clipboard',
  'chat.fb.copied': 'Message copied to clipboard',
  'chat.fb.copiedBang': 'Message copied to clipboard!',
  'chat.fb.good': 'Mark as good response',
  'chat.fb.removeGood': 'Remove positive feedback',
  'chat.fb.bad': 'Mark as bad response',
  'chat.fb.removeBad': 'Remove negative feedback',
  'chat.fb.resend': 'Resend this message',
  'chat.fb.resent': 'Message resent',
  'chat.fb.submittedPositive': 'Positive feedback submitted',
  'chat.fb.submittedNegative': 'Negative feedback submitted',
  'chat.fb.removed': 'Feedback removed',
  'chat.fb.retrying': 'Network error. Retrying... ({attempt}/{max})',
  'chat.fb.submitFailed': 'Failed to submit feedback. Please try again.',
  'chat.fb.timeout': 'Request timed out. Please check your connection and try again.',
  'chat.fb.deleted': 'This message may have been deleted. Please refresh the page.',
  'chat.fb.sessionExpired': 'Session expired. Please refresh the page and try again.',
  'chat.fb.notFound': 'Message not found. It may have been deleted.',
  'chat.fb.forbidden': 'You do not have permission to provide feedback on this message.',
  'chat.fb.manyAttempts': 'Failed to submit feedback after multiple attempts. Please try again later.',
  'chat.fb.submitFailedShort': 'Failed to submit feedback',
  'chat.fb.waitFeedback': 'Please wait for the message to finish generating before providing feedback.',
  'chat.fb.waitCopy': 'Please wait for the message to finish generating before copying.',
  'chat.fb.copyManual': 'Failed to copy message. Please try selecting and copying manually.',
  'chat.fb.copyFailedShort': 'Failed to copy message',
  'chat.fb.copyFailed': 'Failed to copy message. Please try again.',
  'chat.fb.copyTimeout': 'Copy operation timed out. Please try again.',
  'chat.fb.clipboardDenied': 'Clipboard access denied. Please check your browser permissions.',
  'chat.fb.clipboardBlocked': 'Clipboard access blocked for security reasons. Please copy manually.',

  // Voice input
  'chat.voice.disabled': 'Voice input disabled',
  'chat.voice.listeningAria': 'Dictating — press Esc to cancel',
  'chat.voice.input': 'Voice input',
  'chat.voice.listening': 'Listening',
  'chat.voice.hint': 'Listening…',
  'chat.voice.cancel': 'Cancel',
  'chat.voice.confirm': 'Done — insert text',

  // Charts
  'chat.chart.bar': 'Bar',
  'chat.chart.line': 'Line',
  'chat.chart.area': 'Area',
  'chat.chart.pie': 'Pie',
  'chat.chart.doughnut': 'Donut',
  'chat.chart.type': 'Chart type',
  'chat.chart.typeChart': '{type} chart',
  'chat.chart.hideData': 'Hide data',
  'chat.chart.showData': 'Show data',
  'chat.chart.hideDataTable': 'Hide data table',
  'chat.chart.showDataTable': 'Show data table',
  'chat.chart.fullscreen': 'Fullscreen',
  'chat.chart.exitFullscreen': 'Exit fullscreen',
  'chat.chart.viewFullscreen': 'View fullscreen',
  'chat.chart.closeFullscreen': 'Close fullscreen',
  'chat.chart.download': 'Download',
  'chat.chart.downloadChart': 'Download chart',
  'chat.chart.downloadPng': 'Download PNG',
  'chat.chart.summary': 'Chart summary',
  'chat.chart.legend': 'Legend',
  'chat.chart.label': 'Label',
  'chat.chart.series': 'Series {n}',
  'chat.chart.total': 'Total',
  'chat.chart.showSlice': 'Show {name}',
  'chat.chart.hideSlice': 'Hide {name}',
  'chat.chart.aria': '{title}. {type} chart of {n} items',
  'chat.chart.ariaOne': '{title}. {type} chart of 1 item',
  'chat.chart.latest': 'Latest',
  'chat.chart.since': '{pct} since {label}',
  'chat.chart.categories': '{n} categories',
  'chat.chart.oneCategory': '1 category',
  'chat.chart.top': 'Top',
  'chat.chart.ofTotal': '{pct}% of total',

  // Markdown export
  'chat.export.agent': 'Agent',
  'chat.export.exportedBy': 'Exported by',
  'chat.export.exported': 'Exported',
  'chat.export.attachments': 'Attachments',
  'chat.export.empty': '(empty)',
  'chat.export.chart': '[chart]',

  // Inline errors (services/errors.ts)
  'chat.err.FILE_TOO_LARGE': '{file} is larger than the 50 MB per-file limit.',
  'chat.err.COMBINED_SIZE_EXCEEDED': 'Combined attachments exceed the 50 MB limit.',
  'chat.err.TOO_MANY_FILES': 'You can attach up to 5 files per message.',
  'chat.err.UNSUPPORTED_TYPE': '{file} is not a supported file type. Allowed: PDF, DOCX, XLSX, images, text.',
  'chat.err.INVALID_FILENAME': '{file} has an invalid filename.',
  'chat.err.PRESIGN_FAILED': 'Could not prepare the upload. Please try again.',
  'chat.err.UPLOAD_FAILED': 'Uploading {file} failed. Please try again.',
  'chat.err.S3_REJECTED': '{file} was rejected by storage. Please try again.',
  'chat.err.EXTRACTION_FAILED': 'Could not read {file}. It may be password-protected or corrupt.',
  'chat.err.AGENT_TIMEOUT': "The agent didn't respond in time. Please try again.",
  'chat.err.AGENT_UNAVAILABLE': 'The agent service is unavailable. Please try again in a moment.',
  'chat.err.AGENT_ACCESS_DENIED': 'You no longer have access to this agent. Contact an administrator if you need it back.',
  'chat.err.AGENT_DEACTIVATED': 'This agent is no longer available. Pick another agent to carry on.',
  'chat.err.SESSION_CONFLICT': 'A response is still being generated. Please wait or reconnect.',
  'chat.err.AGENT_CONFIG_ERROR': 'This agent is misconfigured. Please contact an administrator.',
  'chat.err.INVALID_STAGED_KEY': 'One or more uploads could not be verified. Please retry.',
  'chat.err.USAGE_LIMIT': "You've reached today's message limit. It resets at midnight UTC.",
  'chat.err.USAGE_LIMIT_N': "You've reached today's message limit ({n}). It resets at midnight UTC.",
  'chat.err.NETWORK_ERROR': 'Network error. Check your connection and try again.',
  'chat.err.UNKNOWN': 'Something went wrong. Please try again.',
  'chat.err.file': 'file',
};

const ar: Record<keyof typeof en, string> = {
  'chat.newChat': 'محادثة جديدة',
  'chat.searchChats': 'ابحث في المحادثات',
  'chat.noChatsFound': 'لم يتم العثور على محادثات',
  'chat.noHistory': 'لا توجد محادثات بعد',
  'chat.startConversation': 'ابدأ محادثة جديدة!',
  'chat.chatTitle': 'عنوان المحادثة',
  'chat.pinChat': 'تثبيت المحادثة',
  'chat.unpinChat': 'إلغاء تثبيت المحادثة',
  'chat.pinChatNamed': 'تثبيت المحادثة: {title}',
  'chat.unpinChatNamed': 'إلغاء تثبيت المحادثة: {title}',
  'chat.renameChat': 'إعادة تسمية المحادثة',
  'chat.renameChatNamed': 'إعادة تسمية المحادثة: {title}',
  'chat.deleteChat': 'حذف المحادثة',
  'chat.deleteChatNamed': 'حذف المحادثة: {title}',
  'chat.inMessages': 'في الرسائل',
  'chat.searching': 'جارٍ البحث…',
  'chat.matches': '{n} نتائج مطابقة',

  'chat.group.pinned': 'المثبتة',
  'chat.group.today': 'اليوم',
  'chat.group.yesterday': 'أمس',
  'chat.group.week': 'آخر 7 أيام',
  'chat.group.month': 'آخر 30 يوماً',

  'chat.dropFile': 'أفلت الملف هنا لإرفاقه',
  'chat.openSidebar': 'فتح الشريط الجانبي',
  'chat.closeSidebar': 'إغلاق الشريط الجانبي',
  'chat.shareChat': 'مشاركة المحادثة',
  'chat.share': 'مشاركة',
  'chat.noAgentsTitle': 'لا يتوفر وكلاء ذكاء اصطناعي',
  'chat.noAgentsBody': 'يرجى التواصل مع المسؤول للحصول على صلاحية الوصول إلى وكلاء الذكاء الاصطناعي.',
  'chat.greet.morning': 'صباح الخير',
  'chat.greet.afternoon': 'مساء الخير',
  'chat.greet.evening': 'مساء الخير',
  'chat.greet.helpToday': 'كيف يمكنني مساعدتك اليوم؟',
  'chat.greet.assistTonight': 'كيف يمكنني مساعدتك هذا المساء؟',
  'chat.greet.workingLate': 'تعمل لوقت متأخر؟ أنا هنا لمساعدتك.',
  'chat.selectAgentHint': 'اختر وكيلاً لبدء المحادثة',
  'chat.aiAssistant': 'مساعد الذكاء الاصطناعي',
  'chat.aiAgent': 'وكيل الذكاء الاصطناعي',
  'chat.assistantResponding': 'المساعد يكتب الرد',
  'chat.assistantThinking': 'المساعد يفكر',
  'chat.scrollToBottom': 'الانتقال إلى الأسفل',
  'chat.you': 'أنت',

  'chat.prompt.legal1': 'راجع هذا العقد وحدّد أي بنود غير معتادة تتعلق بالتعويض أو المسؤولية',
  'chat.prompt.legal2': 'لخّص الالتزامات الرئيسية والمراحل وشروط الدفع',
  'chat.prompt.legal3': 'قارن بين هاتين الاتفاقيتين واذكر جميع الفروقات الجوهرية',
  'chat.prompt.legal4': 'صِغ اتفاقية عدم إفصاح لمقاول باطن جديد',
  'chat.prompt.default1': 'لخّص المستند المرفق واستخرج بنود العمل',
  'chat.prompt.default2': 'حوّل ملاحظات الاجتماع هذه إلى قائمة مهام منظمة مع المسؤولين عنها',
  'chat.prompt.default3': 'اشرح لي هذا الموضوع كما لو كنت أقدّم إحاطة لأحد التنفيذيين',
  'chat.prompt.default4': 'ساعدني في صياغة بريد إلكتروني مهني لعميل بشأن تأخير',

  'chat.copyMessage': 'نسخ الرسالة',
  'chat.messageCopied': 'تم نسخ الرسالة إلى الحافظة',
  'chat.editMessage': 'تعديل الرسالة',
  'chat.edited': '(معدّلة)',
  'chat.deleteMessage': 'حذف الرسالة',
  'chat.showFull': 'عرض الرد كاملاً',
  'chat.showLess': 'عرض أقل',

  'chat.messagePlaceholder': 'اكتب رسالة إلى {name}...',
  'chat.selectAgentPlaceholder': 'اختر وكيلاً لبدء المحادثة...',
  'chat.addFiles': 'إضافة ملفات',
  'chat.connectors': 'الموصلات',
  'chat.composerTools': 'إضافة ملفات وأدوات',
  'chat.stopGenerating': 'إيقاف التوليد',
  'chat.sendMessage': 'إرسال الرسالة',
  'chat.disclaimer': '{name} يعمل بالذكاء الاصطناعي وقد يخطئ. يرجى التحقق من الإجابات.',
  'chat.dismiss': 'إغلاق',

  'chat.agent.changeAria': 'الوكيل: {name}. تغيير الوكيل',
  'chat.agent.talkingTo': 'تتحدث مع {name}',
  'chat.agent.choose': 'اختر وكيلاً',
  'chat.agent.chooseShort': 'اختر وكيلاً',
  'chat.agent.find': 'ابحث عن وكيل…',
  'chat.agent.filter': 'تصفية الوكلاء',
  'chat.agent.noMatch': 'لا يوجد وكلاء مطابقون لـ «{q}».',
  'chat.agent.list': 'الوكلاء',

  'chat.file.uploading': '{name} (جارٍ الرفع…)',
  'chat.file.open': 'فتح {name}',
  'chat.file.remove': 'إزالة',
  'chat.file.removeNamed': 'إزالة {name}',
  'chat.file.analyzing': 'جارٍ التحليل…',

  'chat.code.copy': 'نسخ الكود',
  'chat.table.saved': 'تم الحفظ في التنزيلات',
  'chat.table.download': 'تنزيل هذا الجدول بتنسيق CSV',
  'chat.table.downloaded': 'تم تنزيل الجدول',
  'chat.table.downloadAria': 'تنزيل هذا الجدول كملف CSV',

  'chat.deleteSessionTitle': 'حذف المحادثة',
  'chat.closeDialog': 'إغلاق النافذة',
  'chat.deleteConfirm': 'هل أنت متأكد من حذف هذه المحادثة؟',
  'chat.messagesCount': '{n} رسائل',
  'chat.deleteWarning': 'لا يمكن التراجع عن هذا الإجراء. سيتم حذف جميع رسائل هذه المحادثة نهائياً.',
  'chat.deleteSession': 'حذف المحادثة',

  'chat.chatWith': 'محادثة مع {name}',
  'chat.shareChatTitle': 'مشاركة المحادثة',
  'chat.sharedChat': 'محادثة مشتركة',
  'chat.checkingShare': 'جارٍ التحقق من حالة المشاركة…',
  'chat.shareLink': 'رابط المشاركة',
  'chat.shareReadOnly': 'للقراءة فقط لأي مستخدم مسجّل في Nesma AI لديه الرابط · يعرض الرسائل حتى {date}',
  'chat.viewedTimes': ' · شوهد {n} مرات',
  'chat.viewedOnce': ' · شوهد مرة واحدة',
  'chat.includeNewer': 'تضمين {n} رسائل أحدث',
  'chat.includeNewerOne': 'تضمين رسالة أحدث واحدة',
  'chat.stopSharing': 'إيقاف المشاركة',
  'chat.shareIntro': 'أنشئ رابطاً للقراءة فقط. يمكن لأي مستخدم مسجّل في Nesma AI لديه الرابط عرض هذه المحادثة بحالتها الحالية — تبقى الرسائل اللاحقة خاصة ما لم تحدّث الرابط. تُعرض المرفقات بأسمائها فقط.',
  'chat.creating': 'جارٍ الإنشاء…',
  'chat.createShareLink': 'إنشاء رابط مشاركة',
  'chat.copyLink': 'نسخ الرابط',
  'chat.linkCopied': 'تم النسخ!',
  'chat.downloadMarkdown': 'تنزيل Markdown',
  'chat.copyConversation': 'نسخ المحادثة',
  'chat.downloadPdf': 'تنزيل PDF',
  'chat.exporting': 'جارٍ التصدير...',
  'chat.viewConversation': 'عرض المحادثة: {url}',
  'chat.viewConversationShort': 'عرض المحادثة',

  'chat.toast.connectorOnFailed': 'تعذّر تفعيل {name}.',
  'chat.toast.connectorOffFailed': 'تعذّر إيقاف {name}.',
  'chat.toast.google.connected': 'تم ربط Google Workspace. يمكن للمساعد الآن استخدام Drive وGmail والتقويم.',
  'chat.toast.google.partial': 'تم ربط Google، لكن لم تتم الموافقة على كل الصلاحيات. ستعمل الأجزاء الموافق عليها فقط.',
  'chat.toast.google.denied': 'تم إلغاء تسجيل الدخول إلى Google.',
  'chat.toast.google.expired': 'استغرق تسجيل الدخول إلى Google وقتًا طويلًا. يرجى المحاولة مرة أخرى.',
  'chat.toast.google.domain': 'يرجى تسجيل الدخول بحساب Google الخاص بالشركة.',
  'chat.toast.google.browser': 'أكمل تسجيل الدخول إلى Google في نفس المتصفح الذي بدأت منه.',
  'chat.toast.google.disabled': 'الموصلات غير مفعّلة لحسابك.',
  'chat.toast.google.limit': 'لقد وصلت إلى الحد الأقصى لعدد الموصلات.',
  'chat.toast.google.failed': 'فشل تسجيل الدخول إلى Google. يرجى المحاولة مرة أخرى.',
  'chat.toast.newChatWith': 'بدأت محادثة جديدة مع {name}.',
  'chat.toast.pinFailed': 'تعذّر تحديث التثبيت. يرجى المحاولة مرة أخرى.',
  'chat.toast.deleteSessionFailed': 'تعذّر حذف المحادثة. يرجى المحاولة مرة أخرى.',
  'chat.toast.deleteFailedTitle': 'فشل الحذف',
  'chat.toast.noAgent': 'اختر وكيلاً أولاً — فكل وكيل مهيّأ لنوع مختلف من المهام.',
  'chat.toast.noAgentTitle': 'لم يتم اختيار وكيل',
  'chat.toast.regenerateFailed': 'تعذّرت إعادة توليد الرد. يرجى المحاولة مرة أخرى.',
  'chat.toast.regenerateFailedTitle': 'فشلت إعادة التوليد',
  'chat.toast.editFailed': 'تعذّر تعديل الرسالة. يرجى المحاولة مرة أخرى.',
  'chat.toast.editFailedTitle': 'فشل التعديل',
  'chat.toast.deleteMessageFailed': 'تعذّر حذف الرسالة. يرجى المحاولة مرة أخرى.',
  'chat.toast.downloadFailed': 'تعذّر تنزيل الملف.',
  'chat.toast.downloadFailedTitle': 'فشل التنزيل',
  'chat.toast.nothingToExport': 'لا يوجد ما يمكن تصديره بعد — أرسل رسالة أولاً.',
  'chat.toast.emptyChat': 'محادثة فارغة',
  'chat.toast.copiedConversation': 'تم نسخ المحادثة كاملة إلى الحافظة بتنسيق Markdown.',
  'chat.toast.clipboardBlocked': 'منع المتصفح الوصول إلى الحافظة. استخدم «تنزيل Markdown» بدلاً من ذلك.',
  'chat.toast.pdfFailed': 'تعذّر تصدير المحادثة إلى PDF. يرجى المحاولة مرة أخرى.',
  'chat.toast.exportFailedTitle': 'فشل التصدير',
  'chat.toast.nothingToShare': 'أرسل رسالة أولاً — لا يوجد ما يمكن مشاركته بعد.',
  'chat.toast.linkUpdated': 'أصبح الرابط يتضمن أحدث الرسائل.',
  'chat.toast.linkUpdatedTitle': 'تم تحديث الرابط',
  'chat.toast.shareFailed': 'تعذّر إنشاء رابط المشاركة.',
  'chat.toast.stoppedSharing': 'لم يعد الرابط يعمل لأي شخص.',
  'chat.toast.stoppedSharingTitle': 'تم إيقاف المشاركة',
  'chat.toast.stopSharingFailed': 'تعذّر إيقاف المشاركة.',

  'chat.palette.label': 'لوحة الأوامر',
  'chat.palette.placeholder': 'ابحث عن الوكلاء أو المحادثات الأخيرة أو الأوامر…',
  'chat.palette.noMatches': 'لا توجد نتائج. جرّب كلمات أخرى.',
  'chat.palette.toNavigate': 'للتنقل',
  'chat.palette.toSelect': 'للاختيار',
  'chat.palette.toClose': 'للإغلاق',
  'chat.palette.group.action': 'الإجراءات',
  'chat.palette.group.agent': 'الوكلاء',
  'chat.palette.group.session': 'المحادثات الأخيرة',
  'chat.palette.group.navigation': 'الانتقال إلى',
  'chat.palette.newChat': 'محادثة جديدة',
  'chat.palette.newChatSub': 'ابدأ محادثة جديدة',
  'chat.palette.shortcuts': 'اختصارات لوحة المفاتيح',
  'chat.palette.shortcutsSub': 'عرض جميع الاختصارات المتاحة',
  'chat.palette.switchAgent': 'التبديل إلى هذا الوكيل',
  'chat.palette.recentChat': 'محادثة حديثة',

  'chat.shortcuts.title': 'اختصارات لوحة المفاتيح',
  'chat.shortcuts.palette': 'فتح لوحة الأوامر',
  'chat.shortcuts.newChat': 'محادثة جديدة',
  'chat.shortcuts.theme': 'تبديل المظهر',
  'chat.shortcuts.focus': 'التركيز على حقل الرسالة',
  'chat.shortcuts.send': 'إرسال الرسالة',
  'chat.shortcuts.newLine': 'سطر جديد في الرسالة',
  'chat.shortcuts.stopVoice': 'إلغاء الإملاء الصوتي',
  'chat.shortcuts.sheet': 'عرض قائمة الاختصارات',
  'chat.shortcuts.close': 'إغلاق النوافذ / إلغاء',

  'chat.fb.group': 'إجراءات تقييم الرسالة',
  'chat.fb.wait': 'انتظر حتى تكتمل الرسالة',
  'chat.fb.copy': 'نسخ الرسالة إلى الحافظة',
  'chat.fb.copied': 'تم نسخ الرسالة إلى الحافظة',
  'chat.fb.copiedBang': 'تم نسخ الرسالة إلى الحافظة!',
  'chat.fb.good': 'تقييم كرد جيد',
  'chat.fb.removeGood': 'إزالة التقييم الإيجابي',
  'chat.fb.bad': 'تقييم كرد سيئ',
  'chat.fb.removeBad': 'إزالة التقييم السلبي',
  'chat.fb.resend': 'إعادة إرسال هذه الرسالة',
  'chat.fb.resent': 'تمت إعادة إرسال الرسالة',
  'chat.fb.submittedPositive': 'تم إرسال التقييم الإيجابي',
  'chat.fb.submittedNegative': 'تم إرسال التقييم السلبي',
  'chat.fb.removed': 'تمت إزالة التقييم',
  'chat.fb.retrying': 'خطأ في الشبكة. جارٍ إعادة المحاولة... ({attempt}/{max})',
  'chat.fb.submitFailed': 'تعذّر إرسال التقييم. يرجى المحاولة مرة أخرى.',
  'chat.fb.timeout': 'انتهت مهلة الطلب. يرجى التحقق من الاتصال والمحاولة مرة أخرى.',
  'chat.fb.deleted': 'ربما تم حذف هذه الرسالة. يرجى تحديث الصفحة.',
  'chat.fb.sessionExpired': 'انتهت الجلسة. يرجى تحديث الصفحة والمحاولة مرة أخرى.',
  'chat.fb.notFound': 'لم يتم العثور على الرسالة. ربما تم حذفها.',
  'chat.fb.forbidden': 'ليست لديك صلاحية تقييم هذه الرسالة.',
  'chat.fb.manyAttempts': 'تعذّر إرسال التقييم بعد عدة محاولات. يرجى المحاولة لاحقاً.',
  'chat.fb.submitFailedShort': 'تعذّر إرسال التقييم',
  'chat.fb.waitFeedback': 'يرجى الانتظار حتى يكتمل توليد الرسالة قبل تقييمها.',
  'chat.fb.waitCopy': 'يرجى الانتظار حتى يكتمل توليد الرسالة قبل نسخها.',
  'chat.fb.copyManual': 'تعذّر نسخ الرسالة. يرجى تحديد النص ونسخه يدوياً.',
  'chat.fb.copyFailedShort': 'تعذّر نسخ الرسالة',
  'chat.fb.copyFailed': 'تعذّر نسخ الرسالة. يرجى المحاولة مرة أخرى.',
  'chat.fb.copyTimeout': 'انتهت مهلة عملية النسخ. يرجى المحاولة مرة أخرى.',
  'chat.fb.clipboardDenied': 'تم رفض الوصول إلى الحافظة. يرجى التحقق من أذونات المتصفح.',
  'chat.fb.clipboardBlocked': 'تم حظر الوصول إلى الحافظة لأسباب أمنية. يرجى النسخ يدوياً.',

  'chat.voice.disabled': 'الإدخال الصوتي معطّل',
  'chat.voice.listeningAria': 'جارٍ الإملاء — اضغط Esc للإلغاء',
  'chat.voice.input': 'الإدخال الصوتي',
  'chat.voice.listening': 'جارٍ الاستماع',
  'chat.voice.hint': 'جارٍ الاستماع…',
  'chat.voice.cancel': 'إلغاء',
  'chat.voice.confirm': 'تم — إدراج النص',

  'chat.chart.bar': 'أعمدة',
  'chat.chart.line': 'خطي',
  'chat.chart.area': 'مساحي',
  'chat.chart.pie': 'دائري',
  'chat.chart.doughnut': 'حلقي',
  'chat.chart.type': 'نوع المخطط',
  'chat.chart.typeChart': 'مخطط {type}',
  'chat.chart.hideData': 'إخفاء البيانات',
  'chat.chart.showData': 'عرض البيانات',
  'chat.chart.hideDataTable': 'إخفاء جدول البيانات',
  'chat.chart.showDataTable': 'عرض جدول البيانات',
  'chat.chart.fullscreen': 'ملء الشاشة',
  'chat.chart.exitFullscreen': 'الخروج من ملء الشاشة',
  'chat.chart.viewFullscreen': 'عرض بملء الشاشة',
  'chat.chart.closeFullscreen': 'إغلاق ملء الشاشة',
  'chat.chart.download': 'تنزيل',
  'chat.chart.downloadChart': 'تنزيل المخطط',
  'chat.chart.downloadPng': 'تنزيل PNG',
  'chat.chart.summary': 'ملخص المخطط',
  'chat.chart.legend': 'وسيلة الإيضاح',
  'chat.chart.label': 'التسمية',
  'chat.chart.series': 'السلسلة {n}',
  'chat.chart.total': 'الإجمالي',
  'chat.chart.showSlice': 'إظهار {name}',
  'chat.chart.hideSlice': 'إخفاء {name}',
  'chat.chart.aria': '{title}. مخطط {type} يضم {n} عناصر',
  'chat.chart.ariaOne': '{title}. مخطط {type} يضم عنصراً واحداً',
  'chat.chart.latest': 'الأحدث',
  'chat.chart.since': '{pct} منذ {label}',
  'chat.chart.categories': '{n} فئات',
  'chat.chart.oneCategory': 'فئة واحدة',
  'chat.chart.top': 'الأعلى',
  'chat.chart.ofTotal': '{pct}% من الإجمالي',

  'chat.export.agent': 'الوكيل',
  'chat.export.exportedBy': 'صدّرها',
  'chat.export.exported': 'تاريخ التصدير',
  'chat.export.attachments': 'المرفقات',
  'chat.export.empty': '(فارغة)',
  'chat.export.chart': '[مخطط]',

  'chat.err.FILE_TOO_LARGE': 'حجم {file} يتجاوز الحد الأقصى لكل ملف وهو 50 ميغابايت.',
  'chat.err.COMBINED_SIZE_EXCEEDED': 'يتجاوز الحجم الإجمالي للمرفقات الحد الأقصى وهو 50 ميغابايت.',
  'chat.err.TOO_MANY_FILES': 'يمكنك إرفاق 5 ملفات كحد أقصى في كل رسالة.',
  'chat.err.UNSUPPORTED_TYPE': 'نوع الملف {file} غير مدعوم. الأنواع المسموح بها: PDF وDOCX وXLSX والصور والنصوص.',
  'chat.err.INVALID_FILENAME': 'اسم الملف {file} غير صالح.',
  'chat.err.PRESIGN_FAILED': 'تعذّر تجهيز الرفع. يرجى المحاولة مرة أخرى.',
  'chat.err.UPLOAD_FAILED': 'فشل رفع {file}. يرجى المحاولة مرة أخرى.',
  'chat.err.S3_REJECTED': 'رفض نظام التخزين الملف {file}. يرجى المحاولة مرة أخرى.',
  'chat.err.EXTRACTION_FAILED': 'تعذّرت قراءة {file}. قد يكون محمياً بكلمة مرور أو تالفاً.',
  'chat.err.AGENT_TIMEOUT': 'لم يستجب الوكيل في الوقت المحدد. يرجى المحاولة مرة أخرى.',
  'chat.err.AGENT_UNAVAILABLE': 'خدمة الوكيل غير متاحة حالياً. يرجى المحاولة بعد قليل.',
  'chat.err.AGENT_ACCESS_DENIED': 'لم تعد لديك صلاحية الوصول إلى هذا الوكيل. تواصل مع المسؤول إذا كنت بحاجة إليه.',
  'chat.err.AGENT_DEACTIVATED': 'هذا الوكيل لم يعد متاحاً. اختر وكيلاً آخر للمتابعة.',
  'chat.err.SESSION_CONFLICT': 'لا يزال الرد قيد التوليد. يرجى الانتظار أو إعادة الاتصال.',
  'chat.err.AGENT_CONFIG_ERROR': 'إعدادات هذا الوكيل غير صحيحة. يرجى التواصل مع المسؤول.',
  'chat.err.INVALID_STAGED_KEY': 'تعذّر التحقق من ملف مرفوع أو أكثر. يرجى إعادة المحاولة.',
  'chat.err.USAGE_LIMIT': 'لقد بلغت الحد اليومي للرسائل. يُعاد ضبطه عند منتصف الليل بتوقيت UTC.',
  'chat.err.USAGE_LIMIT_N': 'لقد بلغت الحد اليومي للرسائل ({n}). يُعاد ضبطه عند منتصف الليل بتوقيت UTC.',
  'chat.err.NETWORK_ERROR': 'خطأ في الشبكة. تحقق من الاتصال وحاول مرة أخرى.',
  'chat.err.UNKNOWN': 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
  'chat.err.file': 'الملف',
};

const chat = { en, ar };
export default chat;
